/**
 * StockOverviewWidget - Combined stock info + price chart widget
 *
 * Smart widget that can:
 * - Display stock info card only (default for basic price queries)
 * - Display price chart only
 * - Display both info + chart combined
 * - Merge multiple API calls
 *
 * Config options:
 * - display: "info" | "chart" | "both" (default: "info")
 * - apis: Array of API configs to call and merge
 */

import { useState, useEffect, useMemo } from 'react';
import StockInfoCard from './StockInfoCard';
import PriceChartWidget from './PriceChartWidget';
import { callWidgetApi } from '../../utils/widgetApi';

/**
 * Fetch stock info (company + quote data)
 */
async function fetchStockInfo(ticker) {
    const result = await callWidgetApi('get_ticker_info', { ticker });
    return result.data || {};
}

/**
 * Fetch historical prices
 */
async function fetchHistoricalPrices(ticker, interval = '1d', days = 90) {
    const now = Math.floor(Date.now() / 1000);
    const from = now - (days * 24 * 60 * 60);

    const result = await callWidgetApi('get_historical_prices_range', {
        tickers: [ticker],
        interval,
        from_timestamp: from,
        to_timestamp: now,
        type: 'stock'
    });
    // Backend always returns {ticker: data} format
    return result.data[ticker] || [];
}

/**
 * Tab component for switching views
 */
function ViewTabs({ activeView, onViewChange, showBoth }) {
    const tabs = showBoth
        ? [
            { id: 'both', label: 'Tổng quan' },
            { id: 'info', label: 'Thông tin' },
            { id: 'chart', label: 'Biểu đồ' }
        ]
        : [
            { id: 'info', label: 'Thông tin' },
            { id: 'chart', label: 'Biểu đồ' }
        ];

    return (
        <div className="flex border-b border-gray-200 dark:border-gray-700">
            {tabs.map(tab => (
                <button
                    key={tab.id}
                    onClick={() => onViewChange(tab.id)}
                    className={`px-4 py-2 text-sm font-medium transition-colors ${
                        activeView === tab.id
                            ? 'border-b-2 border-blue-500 text-blue-600 dark:text-blue-400'
                            : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                    }`}
                >
                    {tab.label}
                </button>
            ))}
        </div>
    );
}

/**
 * Main StockOverviewWidget component
 */
export default function StockOverviewWidget({ data, config }) {
    const [stockInfo, setStockInfo] = useState(data?.stockInfo || null);
    const [priceData, setPriceData] = useState(data?.priceData || null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Determine display mode from config
    const displayMode = config?.display || 'info';

    // If display is 'both', show tabs; otherwise show single view
    const [activeView, setActiveView] = useState(displayMode === 'both' ? 'both' : displayMode);

    // Get ticker from config or data
    const ticker = useMemo(() => {
        const t = config?.params?.tickers;
        const firstTicker = Array.isArray(t) ? t[0] : (typeof t === 'string' ? t : null);
        return firstTicker ||
            config?.params?.ticker ||
            data?.ticker ||
            stockInfo?.ticker;
    }, [config, data, stockInfo]);

    // Fetch data based on display mode
    useEffect(() => {
        if (!ticker) return;

        let cancelled = false;

        async function loadData() {
            setLoading(true);
            setError(null);

            try {
                const promises = [];
                const needsInfo = displayMode === 'info' || displayMode === 'both';
                const needsChart = displayMode === 'chart' || displayMode === 'both';

                if (needsInfo && !stockInfo) {
                    promises.push(fetchStockInfo(ticker).then(data => ({ type: 'info', data })));
                }

                if (needsChart && !priceData) {
                    const interval = config?.params?.interval || '1d';
                    const days = interval === '1h' ? 30 : 90;
                    promises.push(fetchHistoricalPrices(ticker, interval, days).then(data => ({ type: 'price', data })));
                }

                if (promises.length > 0) {
                    const results = await Promise.all(promises);

                    if (cancelled) return;

                    results.forEach(result => {
                        if (result.type === 'info') {
                            setStockInfo(result.data);
                        } else if (result.type === 'price') {
                            setPriceData(result.data);
                        }
                    });
                }
            } catch (err) {
                if (!cancelled) {
                    console.error('StockOverviewWidget error:', err);
                    setError(err.message);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        }

        loadData();

        return () => {
            cancelled = true;
        };
    }, [ticker, displayMode, stockInfo, priceData, config]);

    // Loading state
    if (loading && !stockInfo && !priceData) {
        return (
            <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                <div className="animate-pulse">
                    <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-4"></div>
                    <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
                </div>
            </div>
        );
    }

    // Error state
    if (error && !stockInfo && !priceData) {
        return (
            <div className="p-4 border border-red-200 dark:border-red-800 rounded-lg bg-red-50 dark:bg-red-900/20">
                <p className="text-red-600 dark:text-red-400">Error: {error}</p>
            </div>
        );
    }

    // Render based on active view
    const renderContent = () => {
        switch (activeView) {
            case 'info':
                return stockInfo ? (
                    <StockInfoCard data={stockInfo} config={config} />
                ) : (
                    <p className="text-gray-500 p-4">Loading stock info...</p>
                );

            case 'chart':
                return priceData ? (
                    <PriceChartWidget data={priceData} config={config} />
                ) : (
                    <p className="text-gray-500 p-4">Loading chart...</p>
                );

            case 'both':
            default:
                return (
                    <div className="space-y-4">
                        {stockInfo && <StockInfoCard data={stockInfo} config={config} />}
                        {priceData && <PriceChartWidget data={priceData} config={config} />}
                        {loading && <p className="text-gray-500 text-sm">Loading...</p>}
                    </div>
                );
        }
    };

    return (
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-white dark:bg-gray-800">
            {/* Show tabs only when display mode is 'both' */}
            {displayMode === 'both' && (
                <ViewTabs
                    activeView={activeView}
                    onViewChange={setActiveView}
                    showBoth={true}
                />
            )}

            {/* Content */}
            <div className={displayMode === 'both' ? 'p-0' : ''}>
                {renderContent()}
            </div>
        </div>
    );
}
